import { fetchAuthSession } from 'aws-amplify/auth';
/*
 * Minimal chat API client for local verification.
 * Assumes backend at http://localhost:8080 and messaging path /api/chat.
 * Token acquisition should be replaced with actual auth util when available.
 */

type ChatMessage = {
  id?: string;
  channelArn: string;
  body: string;
  senderArn?: string;
  createdAt?: string;
};

type GetMessagesParams = {
  channelArn: string;
  bearerArn: string;
};

type AppConfig = {
  apiBaseUrl?: string;
  messagingBasePath?: string; // default '/api/chat'
};

type RuntimeWindow = Window & {
  __APP_CONFIG__?: AppConfig;
  __CONFIG__?: AppConfig;
};

function getRuntimeConfig(): AppConfig {
  const w = window as RuntimeWindow;
  // Try window-injected config
  if (w.__APP_CONFIG__) return w.__APP_CONFIG__ as AppConfig;
  // Fallback: try to read from a global variable populated elsewhere
  if (w.__CONFIG__) return w.__CONFIG__ as AppConfig;
  return {};
}

async function getAuthToken(): Promise<string | null> {
  // Use Amplify Auth current session; fallback to localStorage
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString?.();
    if (idToken) return idToken as string;
    const accessToken = session.tokens?.accessToken?.toString?.();
    if (accessToken) return accessToken as string;
  } catch (e) {
    logDebug('Amplify Auth token fetch failed, falling back to localStorage', e);
  }
  try {
    const token = localStorage.getItem('authToken');
    return token || null;
  } catch {
    return null;
  }
}

function buildBaseUrl(): string {
  const cfg = getRuntimeConfig();
  const base = cfg.apiBaseUrl || 'http://localhost:8090';
  const path = cfg.messagingBasePath || '/api/chat';
  return `${base}${path}`; // if base='', resolves to same-origin '/api/chat'
}

export async function getMessages(params: GetMessagesParams): Promise<ChatMessage[]> {
  const { channelArn, bearerArn } = params;
  const base = buildBaseUrl();
  const url = `${base}/messages?channelArn=${encodeURIComponent(channelArn)}&bearerArn=${encodeURIComponent(bearerArn)}`;

  const token = await getAuthToken();
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'same-origin',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logWarn('getMessages failed', { status: res.status, url, body: text });
    throw new Error(`getMessages failed: ${res.status}`);
  }
  return res.json();
}

export async function postMessage(message: ChatMessage): Promise<ChatMessage> {
  const base = buildBaseUrl();
  const url = `${base}/messages`;
  const token = await getAuthToken();

  const form = new URLSearchParams();
  form.set('channelArn', message.channelArn);
  if (message.senderArn) form.set('senderArn', message.senderArn);
  form.set('body', message.body);

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form.toString(),
    credentials: 'same-origin',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logWarn('postMessage failed', { status: res.status, url, body: text });
    throw new Error(`postMessage failed: ${res.status}`);
  }
  return res.json();
}

// Quick smoke helper (optional):
export async function smokeChatApi(
  channelArn = 'demo-channel-arn',
  bearerArn = 'demo-bearer-arn',
  senderArn = 'demo-sender-arn'
) {
  try {
    const got = await getMessages({ channelArn, bearerArn });
    logInfo('getMessages ok', got);
    const posted = await postMessage({ channelArn, body: 'hello from chatClient', senderArn });
    logInfo('postMessage ok', posted);
  } catch (e) {
    logError('smokeChatApi error', e);
  }
}

// Simple logger wrappers to satisfy eslint no-console while preserving dev diagnostics
function logDebug(...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug(...args);
  }
}
function logInfo(...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}
function logWarn(...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
}
function logError(...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
}
